import { useState } from 'react';

import { CreateTreatmentEpisodeModal, StatusCell } from '@equip.health/ui';
import { CreateTreatmentEpisodeModalValues } from '@equip.health/ui/lib/types/src/maud/modals/CreateTreatmentEpisode/CreateTreatmentEpisodeModal';

import useEditInquiryStatus from '~/components/common/inquiry/useEditInquiryStatus';
import InquiryCloseModal from '~/components/maud/inquiries/InquiryDetail/InquiryCloseModal';
import {
  getTreatmentEpisodePrograms,
  getTreatmentProgramRules,
  handleAdmitDateOrProgramChange,
  toLocalTz,
} from '~/components/maud/patients/treatmentEpisode/util';

interface InquiryStatusCellProps {
  inquiryId: string;
  inquiryStatus: string;
  version: string;
  targetTreatmentProgram?: string;
  patientId?: string;
}

const InquiryStatusCell = ({
  inquiryId,
  inquiryStatus,
  targetTreatmentProgram,
  version,
  patientId,
}: InquiryStatusCellProps) => {
  const [keyCounter, setKeyCounter] = useState<number>(0);

  const useEditInquiryStatusState = useEditInquiryStatus({
    currentStatus: inquiryStatus,
    showConflictErrorModal: true,
    source: 'Inquiry List',
  });

  const handleOnCancel = () => {
    useEditInquiryStatusState.cancel();
    setKeyCounter(keyCounter + 1);
  };

  const handleCloseInquiryConfirm = async ({
    closedReasonId,
    noShowEmail,
  }: {
    closedReasonId: string;
    noShowEmail?: boolean;
  }) => {
    await useEditInquiryStatusState.closeInquiry({
      closedReasonId,
      inquiryId,
      noShowEmail,
      version,
    });
  };

  const handleOptionClick = async (optionId: string) => {
    await useEditInquiryStatusState.changeStatus(inquiryId, optionId, version);
  };

  const handleEnroll = async ({
    admitDate,
    endDate,
    treatmentProgram,
  }: CreateTreatmentEpisodeModalValues) => {
    await useEditInquiryStatusState.enroll(
      inquiryId,
      toLocalTz(admitDate),
      version,
      getTreatmentEpisodePrograms(admitDate, treatmentProgram, endDate),
      undefined,
      patientId ?? 'test-patient-id',
    );
  };

  return (
    <>
      <StatusCell
        id={`inquiry-${inquiryId}`}
        onOptionClick={handleOptionClick}
        key={keyCounter}
        status={inquiryStatus}
        statusOptions={useEditInquiryStatusState.statusOptions}
      />

      <InquiryCloseModal
        isOpen={useEditInquiryStatusState.showClosedReasonModal}
        onCancel={handleOnCancel}
        onConfirm={handleCloseInquiryConfirm}
      />
      {useEditInquiryStatusState.showCreateTreatmentEpisodeModal && (
        <CreateTreatmentEpisodeModal
          defaultTreatmentProgram={targetTreatmentProgram}
          isOpen={useEditInquiryStatusState.showCreateTreatmentEpisodeModal}
          onCancel={handleOnCancel}
          onChangeAdmitDate={handleAdmitDateOrProgramChange}
          onChangeTreatmentProgram={handleAdmitDateOrProgramChange}
          onConfirm={handleEnroll}
          programRules={getTreatmentProgramRules(
            useEditInquiryStatusState.treatmentProgramOptions,
          )}
        />
      )}
    </>
  );
};

export default InquiryStatusCell;
